/**
 * JoystickPad — left-thumb movement pad (prototype shell).
 *
 * Captures one pointer per drag with `setPointerCapture`, projects the
 * displacement through `computeMoveVector` (screen space, deadzone, clamped to
 * 1) and forwards it to the adapter. Release/cancel always emits a neutral
 * `onMove` + `onMoveEnd`, so a lost touch can never leave movement stuck.
 *
 * No keyboard events, no InputSystem access — the adapter is the boundary.
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  JOYSTICK_DEFAULT_DEADZONE,
  JOYSTICK_DEFAULT_DIAMETER_PX,
  computeMoveVector,
  formatMoveIntent,
} from './joystickMath';
import type { TouchControlAdapter } from './types';

export interface JoystickPadProps {
  adapter: TouchControlAdapter;
  diameterPx?: number;
  deadzone?: number;
  /** Prototype readout hook; receives short human-readable labels. */
  onTelemetry?: (label: string) => void;
  style?: React.CSSProperties;
}

const NEUTRAL = { x: 0, y: 0 } as const;

export const JoystickPad: React.FC<JoystickPadProps> = ({
  adapter,
  diameterPx = JOYSTICK_DEFAULT_DIAMETER_PX,
  deadzone = JOYSTICK_DEFAULT_DEADZONE,
  onTelemetry,
  style,
}) => {
  const [vector, setVector] = useState<{ x: number; y: number }>(NEUTRAL);
  const [active, setActive] = useState(false);
  const pointerIdRef = useRef<number | null>(null);
  const baseRef = useRef<HTMLDivElement | null>(null);

  const applyPoint = useCallback((clientX: number, clientY: number) => {
    const rect = baseRef.current?.getBoundingClientRect();
    if (!rect) return;
    const next = computeMoveVector({
      centerX: rect.left + rect.width / 2,
      centerY: rect.top + rect.height / 2,
      radius: rect.width / 2,
      pointX: clientX,
      pointY: clientY,
      deadzone,
    });
    setVector(next);
    adapter.onMove(next);
    onTelemetry?.(`move ${formatMoveIntent(next)}`);
  }, [adapter, deadzone, onTelemetry]);

  const release = useCallback(() => {
    if (pointerIdRef.current === null) return;
    pointerIdRef.current = null;
    setActive(false);
    setVector(NEUTRAL);
    adapter.onMove(NEUTRAL);
    adapter.onMoveEnd();
    onTelemetry?.('move end');
  }, [adapter, onTelemetry]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    pointerIdRef.current = e.pointerId;
    setActive(true);
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch { /* capture unsupported (jsdom / older engines) — handlers still fire */ }
    applyPoint(e.clientX, e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    e.preventDefault();
    applyPoint(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    release();
  };

  const knobTravel = Math.max(8, diameterPx / 2 - 26);
  const knobStyle: React.CSSProperties = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: 48,
    height: 48,
    marginLeft: -24,
    marginTop: -24,
    borderRadius: '50%',
    background: active ? 'rgba(126, 200, 255, 0.85)' : 'rgba(255, 255, 255, 0.45)',
    border: '2px solid rgba(255, 255, 255, 0.8)',
    transform: `translate(${vector.x * knobTravel}px, ${vector.y * knobTravel}px)`,
    transition: active ? 'none' : 'transform 120ms ease-out',
    pointerEvents: 'none',
  };

  return (
    <div
      ref={baseRef}
      role="group"
      aria-label="Movement pad (touch prototype, stub adapter)"
      data-testid="touch-move-pad"
      data-active={active ? 'true' : 'false'}
      data-vector={`${vector.x.toFixed(2)},${vector.y.toFixed(2)}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={release}
      onLostPointerCapture={release}
      style={{
        width: diameterPx,
        height: diameterPx,
        borderRadius: '50%',
        background: 'rgba(12, 22, 34, 0.45)',
        border: `2px solid ${active ? 'rgba(126, 200, 255, 0.9)' : 'rgba(255, 255, 255, 0.35)'}`,
        boxSizing: 'border-box',
        pointerEvents: 'auto',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
    >
      <div aria-hidden="true" style={knobStyle} />
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -18,
          width: '100%',
          textAlign: 'center',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: 'rgba(255, 255, 255, 0.75)',
          pointerEvents: 'none',
        }}
      >
        MOVE · PROTOTYPE
      </span>
    </div>
  );
};
